export type Profile = {
    uuid?: string;
    id: number;
    product: string;
    hash: string;
    language: string;
    started?: string;
    groupChart?: boolean;
};

export type User = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    emailVerified: boolean;
    enabled: boolean;
    createdTimestamp: string;
    requiredActions: string[];
};

import { Locale } from "vue-i18n";

export type CustomerPurchaseProjection = {
    email: string;
    firstname: string;
    lastname: string;
    productProduct: string;
    quantity: number;
    distributorCompany: string;
}

export type DistributorPurchaseProjection = {
    company: string;
    productProduct: string;
    quantity: number;
}

export type PurchasedProduct = {
    id: number;
    product: string;
    quantity: number;
    trainer: string;
};

export type Identifier = {
    id: number;
    identifier: string;
    texts: Text[];
};

export type Text = {
    id: number;
    text: string;
    language: string;
};

export type Product = {
    productNo: string;
    shopLink: string;
    active: boolean;
    model: Model;
};

export type ProductActivation = {
    id: number;
    name: string;
    languages: string[];
}

export type Model = {
    model: string;
    rank: number;
};

export type TranslatedProduct = {
    productName: string;
    productNo: string;
    quantity: number;
    shopLink: string;
    modelName: string;
    modelNo: string;
    rank: number;
    profileLanguages: string[];
    questionnaireLanguages: string[];
};

export type LanguageProduct = {
    id: number;
    name: string;
    languages: string[];
};

export type ProductLanguages = { [key: string]: string[] };

export type Trainer = {
    company: string;
    language: string;
    recipient: string;
    distributor: Distributor;
    purchaseLimit: number;
    isDistributorAccount: boolean;
    email: string;
    firstname: string;
    lastname: string;
    address: Address | null;
    id: string;
    timeZone: string;
    isStockKeepingDistributor: boolean;
    portrait: string;
    logo: string;
    beta: boolean;
    standardRetentionPeriod: string;
    deletionNotification: boolean;
};

export type Distributor = {
    id: number;
    company: string;
    monthlyBillingEnabled: boolean;
};

export type Address = {
    country: string;
    postalCode: string;
    city: string;
    street: string;
    website: string;
    phoneNo: string;
    id: number;
};

export type MonthlyPurchase = {
    id?: number;
    product: string;
    quantity: number;
    trainer: string;
    date: string;
};

export type ExtendedMonthlyPurchase = {
    id?: number;
    productDescription: string;
    quantity: number;
    trainer: string;
    email: string;
    date: string;
    manual: boolean;
};

export type MonthlyTransfer = {
    id?: number;
    product: string;
    quantity: number;
    trainer: string;
    recipient: string;
    date: string;
    manual: boolean;
};

export type ExtendedMonthlyTransfer = {
    id?: number;
    productDescription: string;
    quantity: number;
    trainer: string;
    recipient: string;
    email: string;
    date: string;
    manual: boolean;
};

export enum Recipients {
    TRAINER = "TRAINER",
    PARTICIPANT = "PARTICIPANT",
    BOTH = "BOTH",
}

export enum Rights {
    PROFILES = "PROFILES",
    PROJECTS = "PROJECTS",
}

export type Participants = {
    participants: Participant[];
};

/** How a participant's result came about (RDT-400): answered the questionnaire, or typed in by the trainer. */
export type InputMode = "QUESTIONNAIRE" | "DIRECT_INPUT";

export type Participant = {
    id?: number;
    status?: string;
    gender?: string;
    email: string;
    firstName: string;
    name: string;
    questionnaire?: string;
    projectId: number;
    selectedProduct: string;
    departmentId?: number;
    roleId?: number;
    deletionDate?: string | null;
    score?: number;
    /** Unset until a result exists; cleared again by a support reset. */
    inputMode?: InputMode | null;
};

/**
 * A participant waiting for profile generation (GET /participants/pending): the participant plus the
 * project it belongs to (its language/trainer/name are shown in the pending views and used to regenerate)
 * and completedDate — when it finished (questionnaire completed / direct input saved).
 */
export type PendingParticipant = Participant & {
    project: Project;
    completedDate: string | null;
};

export type GroupChart = {
    products: Products;
    groupChart: string;
};

export type Products = {
    products: Product[];
};

/**
 * Participants role e.g. for IN119
 */
export type ProjectRole = {
    id?: number;
    role: string;
    name: string;
};

export type Role = {
    product: string;
    type: string;
};

export type ParticipantActivity = {
    id?: number;
    type: string;
    date: string;
    /** Only set on PROFILE_GENERATED activities (RDT-314); shown in the activity label. */
    profileLanguage?: string;
};

/**
 * What the mail provider (Scaleway) knows about the emails sent to one address (RDT-360). Read live
 * from the e-mail-service; nothing here is stored on our side.
 */
export type DeliveryReport = {
    recipient: string;
    /** Newest first. */
    emails: DeliveredEmail[];
    /** Non-empty when the address is blocked — then nothing sent to it is delivered at all. */
    blocks: DeliveryBlock[];
    /**
     * False when a recorded email could not be looked up, so a row this report should have had is
     * missing (RDT-384).
     */
    complete: boolean;
};

export type DeliveredEmail = {
    id: string;
    /** Scaleway's own message id — the one the e-mail-service records at send time (RDT-365). */
    messageId: string;
    /**
     * True when the e-mail-service recorded this email for the participant or trainer that was asked
     * about. False means it went to the same address but belongs to something else, or predates the
     * recording — either way, not known to be theirs.
     */
    attributed: boolean;
    subject: string;
    sentAt: string | null;
    updatedAt: string | null;
    status: DeliveryState;
    statusDetails: string | null;
    tryCount: number;
    flags: DeliveryFlag[];
    attempts: DeliveryAttempt[];
};

/** One handover attempt, with the recipient server's own SMTP answer. */
export type DeliveryAttempt = {
    rank: number;
    triedAt: string | null;
    /** 0 when the attempt never reached an SMTP server. */
    code: number;
    message: string;
};

export type DeliveryBlock = {
    email: string;
    type: string;
    reason: string | null;
    endsAt: string | null;
    /** True when someone blocked the address by hand rather than Scaleway blocking it after a bounce. */
    custom: boolean;
};

/**
 * Scaleway's delivery states. SENT means the recipient's server accepted the message — which is more
 * than our own "sent", which only means Scaleway accepted it from us.
 */
export enum DeliveryState {
    UNKNOWN = "UNKNOWN",
    NEW = "NEW",
    SENDING = "SENDING",
    SENT = "SENT",
    FAILED = "FAILED",
    CANCELED = "CANCELED",
}

/** Why an email failed. HARD_BOUNCE, MAILBOX_NOT_FOUND, MAILBOX_FULL, SPAM and BLOCKLISTED need action. */
export enum DeliveryFlag {
    UNKNOWN_FLAG = "UNKNOWN_FLAG",
    SOFT_BOUNCE = "SOFT_BOUNCE",
    HARD_BOUNCE = "HARD_BOUNCE",
    SPAM = "SPAM",
    MAILBOX_FULL = "MAILBOX_FULL",
    MAILBOX_NOT_FOUND = "MAILBOX_NOT_FOUND",
    GREYLISTED = "GREYLISTED",
    SEND_BEFORE_EXPIRATION = "SEND_BEFORE_EXPIRATION",
    BLOCKLISTED = "BLOCKLISTED",
}

/** Whose emails to ask about (RDT-365); always the recipient of the email. */
export enum EmailReferenceType {
    PARTICIPANT = "PARTICIPANT",
    TRAINER = "TRAINER",
}

/**
 * The emails support can send again (RDT-38). Sent to `POST /participants/{id}/resend-email` as the
 * `type` query parameter and matched by name, so these values must stay in step with the backend.
 */
export enum ResendableEmail {
    QUESTIONNAIRE = "QUESTIONNAIRE",
    PROFILE = "PROFILE",
}

// One shared declaration in @persolog/utils, re-exported so existing imports keep working: three
// copies of this enum are what let the frontends drift out of sync with the backend (RDT-389).
export { ParticipantActivityType } from "@persolog/utils";

export type Project = {
    id?: number;
    name: string;
    description?: string;
    product: string;
    defaultProduct: string;
    language: Locale;
    trainer?: string;
    anonymous?: boolean;
    focus?: string;
    recipients?: Recipients;
    additionalRecipient?: Recipient | null;
    status?: string;
    creation?: string;
    editedDate?: string;
    archiveDate?: string;
    deletionDate?: string | null;
    participantDeletionDate?: string | null;
    participantRetentionPeriod: string;
    participantDeletionNotification: boolean;
    recipientReleaseDate?: string | null;
};

export type Department = {
    id?: number | null;
    name: string;
};

export type Recipient = {
    email: string;
    firstName: string;
    lastName: string;
};

export type Tag = {
    id?: number;
    value?: string;
};

export type Tags = {
    tags?: Tag[];
};

export type DirectLink = {
    id?: number;
    project: Project;
    quantity: number;
    hash?: string;
    depleted?: boolean;
};

export type QuestionnaireParticipant = {
    id: number;
    hash: string;
    trainer: string;
    status: string;
    focus: string;
};

export type Questionnaire = {
    id: string;
    hasFocus: boolean;
};

export type AssociatedAccount = {
    id?: number;
    trainer: Trainer;
    associatedAccount: Trainer;
    rights: string;
};

export interface ValidateableComponent {
    validate: () => boolean;
}

export interface StepNavItem {
    label: string;
    icon?: string;
    disabled: boolean;
    props: Record<string, unknown>;
    [key: string]: unknown;
}

export type LinkedAccountData = {
    id?: number;
    trainerEmail: string;
    associatedAccountId: string;
    rights: string;
};

export interface AssociatedDirectInput {
    id: number;
    input: DirectInput;
}

export interface DirectInput {
    product: string;
    dimensions: Dimension[];
}

export interface Dimension {
    id: string;
    name: string;
    competencies: Competency[];
}

export interface Competency {
    id: string;
    name: string;
    values: number[];
}

export interface ProductCalculationResults {
    id: number;
    calculations: ProductCalculationResult[];
}

export interface ProductCalculationResult {
    product: string;
    dimensionCalculationResults: DimensionCalculationResult[];
    level: number;
    sum: number;
}

export interface DimensionCalculationResult {
    identifier: string;
    dimension: string;
    competencyCalculationResults: CompetencyCalculationResult[];
    level: number;
    sum: number;
}

export interface CompetencyCalculationResult {
    identifier: string;
    competency: string;
    level: number;
    sum: number;
}

export type Email = {
    type: EmailType;
    profile: string;
    link: string;
    trainer: EmailTrainer;
    participant?: EmailParticipant; // Optional because of the default value
    additionalRecipient?: EmailRecipient; // Optional because of the default value
    profileRecipient: ProfileRecipient;
};

export type EmailTrainer = {
    firstname: string;
    lastname: string;
    company: string;
    email: string;
};

export type EmailRecipient = {
    firstname: string;
    lastname: string;
    email: string;
};

export type EmailParticipant = {
    firstname: string;
    lastname: string;
    email: string;
};

export type CrossSell = {
    products: string[];
    crossSellProducts: CrossSellProduct[];
};

export type CrossSellProduct = {
    product: string;
    picture: string;
};

export type DomainText = {
    identifier: string;
    text: string;
};

export type DomainTexts = {
    domainTexts: DomainText[];
};

/** One text-search match: an identifier/text pair and where it lives (namespace + language). */
export type TextSearchHit = {
    product: string;
    language: string;
    identifier: string;
    text: string;
};

export type Snapshot = {
    product: string;
    language: string;
    version: number;
    createdAt: string;
    createdBy: string;
    texts: DomainText[];
};

export enum EmailType {
    QUESTIONNAIRE = "QUESTIONNAIRE",
    TRAINER_PORTAL = "TRAINER_PORTAL",
    PARTICIPANT_PORTAL = "PARTICIPANT_PORTAL",
    ADDITIONAL_RECIPIENT = "ADDITIONAL_RECIPIENT",
    DIRECT_LINK = "DIRECT_LINK",
    NEW_ACCOUNT = "NEW_ACCOUNT",
    ORDER = "ORDER",
    GROUP_CHART = "GROUP_CHART",
    SELF_EVALUATION_DOI = "SELF_EVALUATION_DOI",
}

export enum ProfileRecipient {
    TRAINER = "TRAINER",
    PARTICIPANT = "PARTICIPANT",
    BOTH = "BOTH",
}

export type Transaction = {
    id?: number;
    trainer: string;
    type: BalanceActivityType;
    date: string;
    quantity: number;
    product: Product;
    productDescription?: string;
    oldBalance: number;
    newBalance: number;
};

export enum BalanceActivityType {
    PURCHASE_MONTHLY_BILLING,
    PURCHASE_SHOP,
    CANCELLATION_SHOP,
    USE_PROFILE,
    REFUND_PROFILE,
    SUPPORT,
    SALE_TO_SUB_ACCOUNT,
    PURCHASE_FROM_DISTRIBUTOR,
    TRANSFER_SENT,
    TRANSFER_RECEIVED,
    RETRIEVAL_FROM_SUB_ACCOUNT,
    RETRIEVAL_BY_DISTRIBUTOR,
}
